import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { formatHeat } from "@/components/feature/TopicCard";
import DiscussionSection from "./components/DiscussionSection";
import RelatedTopics from "./components/RelatedTopics";
import { topics } from "@/mocks/topics";
import { useSeo } from "@/hooks/useSeo";
import type { Topic } from "@/types";

const allTopics: Topic[] = topics;
const hotList = [...allTopics].sort((a, b) => b.heat - a.heat).slice(0, 5);

export default function TopicPage() {
  const { id } = useParams();
  const topic = allTopics.find((item) => item.id === id);

  const jsonLd = topic
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Article",
            headline: topic.title,
            description: topic.excerpt,
            articleSection: topic.categoryName,
            keywords: topic.tags.join(","),
            inLanguage: "zh-CN",
            image: topic.cover,
            author: { "@type": "Organization", name: "黑料专区" },
            publisher: { "@type": "Organization", name: "黑料专区" },
            mainEntityOfPage: `https://leiysp.cn/topic/${topic.id}`,
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://leiysp.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "话题广场",
                item: "https://leiysp.cn/topics",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: topic.title,
                item: `https://leiysp.cn/topic/${topic.id}`,
              },
            ],
          },
        ],
      }
    : undefined;

  useSeo({
    title: topic ? `${topic.title} - 吃瓜讨论 | 黑料专区` : "话题详情 - 黑料专区",
    description: topic
      ? `${topic.excerpt} 在黑料专区查看该吃瓜话题的背景梳理、瓜友观点碰撞与最新后续。`
      : "黑料专区话题详情页，查看吃瓜话题背景、瓜友讨论与后续进展。",
    keywords: topic
      ? `${topic.tags.join(",")},黑料专区,吃瓜讨论,瓜友讨论`
      : "黑料专区,吃瓜讨论,瓜友讨论",
    path: topic ? `/topic/${topic.id}` : "/topics",
    ogType: "article",
    jsonLd,
  });

  if (!topic) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="mx-auto flex w-full max-w-[1280px] flex-1 flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这个话题可能已经下线
          </h1>
          <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
            你访问的话题不存在或已调整，去话题广场看看其他正在热议的内容吧。
          </p>
          <Link
            to="/topics"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回话题广场
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-8 md:px-6 md:py-10">
        <nav aria-label="面包屑" className="text-xs text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li>
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line"></i>
            </li>
            <li>
              <Link to="/topics" className="transition-colors hover:text-primary-600">
                话题广场
              </Link>
            </li>
            <li>
              <i className="ri-arrow-right-s-line"></i>
            </li>
            <li className="text-foreground-800">{topic.categoryName}</li>
          </ol>
        </nav>

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-12">
          <article>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-price-tag-3-line"></i>
              {topic.categoryName}
            </span>

            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {topic.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {topic.time}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-accent-500"></i>
                {formatHeat(topic.heat)} 热度
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line"></i>
                {topic.comments} 条讨论
              </span>
            </div>

            <div className="mt-6 h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
              <img
                src={topic.cover}
                alt={`${topic.title} 话题配图`}
                title={`${topic.title} - 吃瓜讨论`}
                className="h-full w-full object-top"
              />
            </div>

            <p className="mt-6 rounded-lg border-l-4 border-primary-400 bg-background-100 px-5 py-4 text-sm leading-7 text-foreground-700">
              {topic.excerpt}
            </p>

            {topic.content.map((section) => (
              <section key={section.heading} className="mt-8">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 18)}
                    className="mt-4 text-sm leading-8 text-foreground-700"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            <div className="mt-8 flex flex-wrap gap-2">
              {topic.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/topics?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-medium text-secondary-700 transition-colors hover:bg-secondary-200"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <DiscussionSection topicId={topic.id} />
          </article>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                话题速览
              </h2>
              <dl className="mt-4 flex flex-col gap-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">所属分类</dt>
                  <dd className="font-medium text-foreground-900">{topic.categoryName}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">当前热度</dt>
                  <dd className="font-medium text-accent-600">{formatHeat(topic.heat)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">讨论条数</dt>
                  <dd className="font-medium text-foreground-900">{topic.comments}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-600">更新于</dt>
                  <dd className="font-medium text-foreground-900">{topic.time}</dd>
                </div>
              </dl>
              <Link
                to={`/topics?category=${topic.categoryId}`}
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
              >
                <i className="ri-layout-grid-line"></i>
                浏览同分类
              </Link>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">
                热议榜
              </h2>
              <ol className="mt-4 flex flex-col gap-3">
                {hotList.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/topic/${item.id}`} className="group flex items-start gap-3">
                      <span
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                          index < 3
                            ? "bg-accent-500 text-background-50"
                            : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="line-clamp-2 text-sm leading-6 text-foreground-700 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>

        <RelatedTopics current={topic} all={allTopics} />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}